import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { retrieveAllDogs } from "../api/retrieveAllDogs";
import { publicDogModel } from "../types/publicDogModel";
import DonateDogCard from "./DonateDogCard";
import { sortDogsByUrgency } from "../utils/sortDogsByUrgency";

const DogBrowserSection: React.FC = () => {
  const navigate = useNavigate();
  const [dogs, setDogs] = useState<publicDogModel[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const DOGS_PER_PAGE = 4;
  const MAX_DOGS = 20;

  useEffect(() => {
    const fetchDogs = async () => {
      try {
        // Fetch all dogs directly
        const allDogs = await retrieveAllDogs();

        // Filter out deactivated dogs (only show active dogs)
        const activeDogs = allDogs.filter((dog: publicDogModel) => dog.active === true);

        // Sort by urgency
        const sorted = sortDogsByUrgency(activeDogs);

        // Filter out duplicates if any and limit to MAX_DOGS
        const uniqueDogs = Array.from(
          new Map(sorted.map((dog: publicDogModel) => [dog.dog_id, dog])).values()
        ).slice(0, MAX_DOGS);

        setDogs(uniqueDogs);
      } catch (error) {
        console.error("Error fetching dogs for landing page:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDogs();
  }, []);

  const totalPages = Math.ceil(dogs.length / DOGS_PER_PAGE);
  const displayedDogs = dogs.slice(
    currentPage * DOGS_PER_PAGE,
    (currentPage + 1) * DOGS_PER_PAGE
  );

  if (loading) {
    return <div className="py-12 text-center">Loading pups...</div>;
  }

  if (dogs.length === 0) {
    return null; // Don't show section if no dogs
  }

  return (
    <section className="bg-blue p-6">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex justify-between p-4">
          <h2 className="text-3xl font-bold text-black text-center">
            Meet Our Pups
          </h2>
          <button
            onClick={() => {
              navigate("/browse");
              window.scrollTo(0, 0);
            }}
            className="flex items-center justify-center rounded-md bg-yellow transition hover:bg-yellow/90"
          >
            <p className="px-4 py-2 font-poppins text-md font-bold text-black">
              Browse All Pups
            </p>
          </button>
        </div>

        <div className="flex flex-wrap justify-center gap-6">
          {displayedDogs.map((dog) => (
            <div key={dog.dog_id} className="transform scale-90 hover:scale-95 transition-transform duration-300">
              <DonateDogCard dog={dog} />
            </div>
          ))}
        </div>

        {/* Dotted Pagination */}
        <div className="flex justify-center gap-3">
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${currentPage === index
                ? "bg-white scale-125"
                : "bg-white/50 hover:bg-white/80"
                }`}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DogBrowserSection;
